import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { authFetch } from '../../api';

const { 
  FiShield, FiUsers, FiActivity, FiKey, FiPlus, 
  FiMail, FiCheckCircle, FiXCircle, FiRefreshCw,
  FiTrash2, FiAlertTriangle, FiLoader, FiUserPlus,
  FiUserCheck, FiUserX, FiPower, FiSend, FiMoreVertical
} = FiIcons;

const DeveloperPortal = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', display_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    const res = await authFetch('admin-invitations');
    
    if (res.status === 'success') {
      // Root cause: authFetch wraps function responses. 
      // If the function returns { status, admins }, authFetch returns { status, data: { status, admins } }
      // We must normalize to find the array in any of these locations.
      const rawData = res.data || res;
      const adminsList = 
        Array.isArray(rawData.admins) ? rawData.admins :
        Array.isArray(rawData.data) ? rawData.data :
        Array.isArray(rawData) ? rawData :
        Array.isArray(res.admins) ? res.admins :
        [];
        
      setAdmins(adminsList);
    } else {
      setError(res.error || 'Failed to load administrators');
    }
    setLoading(false);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await authFetch('admin-invitations', {
      method: 'POST',
      body: JSON.stringify(inviteForm)
    });
    
    if (res.status === 'success') {
      setInviteModalOpen(false);
      setInviteForm({ email: '', display_name: '' });
      fetchAdmins();
      alert('Invitation sent successfully.');
    } else {
      alert(res.error || 'Failed to send invitation');
    }
    setSubmitting(false);
  };

  const handleResendInvitation = async (adminId) => {
    if (!confirm('Resend invitation setup link? Previous tokens will be invalidated.')) return;
    setActionInProgress(adminId);
    const res = await authFetch(`admin-invitations/${adminId}/resend`, { method: 'POST' });
    if (res.status === 'success') {
      alert('Invitation resent successfully.');
    } else {
      alert(res.error || 'Failed to resend invitation');
    }
    setActionInProgress(null);
  };

  const handleDisableAdmin = async (adminId) => {
    if (!confirm('Are you sure you want to disable this administrator? All active sessions will be revoked.')) return;
    setActionInProgress(adminId);
    const res = await authFetch(`admin-invitations/${adminId}/disable`, { method: 'POST' });
    if (res.status === 'success') {
      fetchAdmins();
    } else {
      alert(res.error || 'Failed to disable administrator');
    }
    setActionInProgress(null);
  };

  const handleEnableAdmin = async (adminId) => {
    setActionInProgress(adminId);
    const res = await authFetch(`admin-invitations/${adminId}/enable`, { method: 'POST' });
    if (res.status === 'success') {
      fetchAdmins();
    } else {
      alert(res.error || 'Failed to enable administrator');
    }
    setActionInProgress(null);
  };

  const handleRevokeSessions = async (adminId) => {
    if (!confirm('Revoke all active sessions for this administrator?')) return;
    setActionInProgress(adminId);
    const res = await authFetch(`admin-invitations/${adminId}/revoke-sessions`, { method: 'POST' });
    if (res.status === 'success') {
      alert('All sessions revoked.');
    } else {
      alert(res.error || 'Failed to revoke sessions');
    }
    setActionInProgress(null);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiActivity },
    { id: 'admins', label: 'Administrators', icon: FiUsers },
    { id: 'audit', label: 'Audit Logs', icon: FiShield },
    { id: 'config', label: 'Configuration', icon: FiKey }
  ];

  // Defensive check for admins array
  const safeAdmins = Array.isArray(admins) ? admins : [];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <header className="mb-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-400">
            <SafeIcon icon={FiShield} className="text-2xl" />
          </div>
          <h1 className="text-3xl font-bold">Developer Portal</h1>
        </div>
        <p className="text-gray-400">System security management and administrator provisioning.</p>
      </header>

      <nav className="flex gap-4 border-b border-white/10 mb-8">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-[2px] ${
              activeTab === tab.id 
                ? 'border-indigo-500 text-indigo-400' 
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            <SafeIcon icon={tab.icon} />
            {tab.label}
          </button>
        ))}
      </nav>

      <main>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-sm">
              <h3 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wider">System Status</h3>
              <div className="flex items-center gap-2 text-green-400 font-semibold">
                <SafeIcon icon={FiCheckCircle} />
                All Systems Operational
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-sm">
              <h3 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wider">Active Admins</h3>
              <div className="text-2xl font-bold text-white">
                {safeAdmins.filter(a => a.status === 'active').length}
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-sm">
              <h3 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wider">Security Events</h3>
              <div className="text-2xl font-bold text-white">0 (Last 24h)</div>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-white/10 flex justify-between items-center bg-white/5">
              <div>
                <h2 className="text-xl font-bold text-white">Permanent Administrators</h2>
                <p className="text-sm text-gray-400">Manage business administrator accounts.</p>
              </div>
              <button 
                onClick={() => setInviteModalOpen(true)}
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <SafeIcon icon={FiUserPlus} />
                Invite Admin
              </button>
            </div>

            {loading ? (
              <div className="p-12 flex justify-center">
                <SafeIcon icon={FiLoader} className="text-3xl text-indigo-400 animate-spin" />
              </div>
            ) : error ? (
              <div className="p-12 text-center text-red-400">
                <SafeIcon icon={FiXCircle} className="text-4xl mx-auto mb-2" />
                <p>{error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-white/5 text-xs uppercase text-gray-400 font-semibold">
                    <tr>
                      <th className="px-6 py-4">Administrator</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Joined</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {safeAdmins.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                          No business administrators found.
                        </td>
                      </tr>
                    ) : safeAdmins.map(admin => (
                      <tr key={admin.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-bold">
                              {(admin.display_name || admin.email || 'A').charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-white">{admin.display_name || 'Unnamed Admin'}</div>
                              <div className="text-sm text-gray-400">{admin.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            admin.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            admin.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {admin.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'Pending'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            {admin.status === 'pending' && (
                              <button 
                                title="Resend Invitation"
                                disabled={actionInProgress === admin.id}
                                className="p-2 text-gray-400 hover:text-yellow-400 transition-colors disabled:opacity-50"
                                onClick={() => handleResendInvitation(admin.id)}
                              >
                                <SafeIcon icon={FiSend} />
                              </button>
                            )}
                            
                            {admin.status === 'active' || admin.status === 'pending' ? (
                              <button 
                                title="Disable Account"
                                disabled={actionInProgress === admin.id}
                                className="p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                                onClick={() => handleDisableAdmin(admin.id)}
                              >
                                <SafeIcon icon={FiUserX} />
                              </button>
                            ) : (
                              <button 
                                title="Enable Account"
                                disabled={actionInProgress === admin.id}
                                className="p-2 text-gray-400 hover:text-green-400 transition-colors disabled:opacity-50"
                                onClick={() => handleEnableAdmin(admin.id)}
                              >
                                <SafeIcon icon={FiUserCheck} />
                              </button>
                            )}
                            
                            <button 
                              title="Revoke Sessions"
                              disabled={actionInProgress === admin.id}
                              className="p-2 text-gray-400 hover:text-indigo-400 transition-colors disabled:opacity-50"
                              onClick={() => handleRevokeSessions(admin.id)}
                            >
                              <SafeIcon icon={FiPower} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="bg-white/5 backdrop-blur-sm p-12 rounded-xl border border-white/10 shadow-sm text-center">
            <SafeIcon icon={FiShield} className="text-4xl text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white">Audit logs are currently archived</h3>
            <p className="text-gray-400">Accessing the full security audit requires manual instance export.</p>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-sm max-w-2xl">
            <h3 className="text-lg font-bold text-white mb-6">Security Configuration</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <div className="font-medium text-white">PBKDF2 Iterations</div>
                  <div className="text-sm text-gray-400">Current setting for password hashing</div>
                </div>
                <div className="font-mono text-indigo-400 font-bold">100,000</div>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <div className="font-medium text-white">Session Expiry</div>
                  <div className="text-sm text-gray-400">Maximum duration of a login session</div>
                </div>
                <div className="font-mono text-indigo-400 font-bold">24 Hours</div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Invite Modal */}
      <AnimatePresence>
        {inviteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-white/10"
            >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-indigo-500/10">
                <h3 className="text-lg font-bold text-white">Invite Administrator</h3>
                <button onClick={() => setInviteModalOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                  <SafeIcon icon={FiXCircle} className="text-xl" />
                </button>
              </div>
              <form onSubmit={handleInvite} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={inviteForm.display_name}
                    onChange={e => setInviteForm(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="e.g. Peter Lockwood"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-white placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={e => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="peter@peterlockwood.com"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-white placeholder:text-gray-600"
                  />
                </div>
                <div className="bg-yellow-500/10 p-4 rounded-lg flex gap-3 text-sm text-yellow-200 border border-yellow-500/20">
                  <SafeIcon icon={FiAlertTriangle} className="text-xl shrink-0 text-yellow-500" />
                  <p>An invitation with a one-time setup link will be sent to this email address.</p>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiMail} />}
                  Send Invitation
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeveloperPortal;